import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { fetchUserPermissions } from "../api/userRights.api";
import { revokeUserPermission } from "../api/user.api";
import { RevokePermissionsBody } from "../api/models/permission.models";

// Hook to get all user permissions and revoke them all at once
const useDeleteCompanyUser = ({ companyId, organizationId }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [deletingUser, setDeletingUser] = useState<boolean>(false);
  const [bearerToken, toast] = useUIStore(
    useShallow((state: UIState) => [state.bearerToken, state.toastRef])
  );

  const deleteCompanyUser = (userId: number) => {
    deleteUserMutation.mutate({ userId });
  };

  const deleteUserMutation = useMutation({
    mutationFn: async (payload: { userId: number }) => {
      // do stuff
      const userPermission = await fetchUserPermissions(
        bearerToken as string,
        payload.userId,
        companyId as number,
        organizationId as number
      );

      if (userPermission && userPermission.length > 0) {
        // revoke all permissions, after that user is no longer returned in fetchAllCompanyUsers

        await Promise.all(
          userPermission.map((permission) =>
            revokePermission(
              bearerToken as string,
              {
                userId: payload.userId,
                permissions: [permission.id],
                companyId: companyId as number,
              } as RevokePermissionsBody,
              companyId as number
            )
          )
        );

        return Promise.resolve(true);
      }

      return Promise.resolve(true);
    },
    onError: (error) => {
      console.error("Failed to delete user:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("label-user-delete-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("label-user-deleted"),
        life: 3000,
      });
      queryClient.invalidateQueries({ queryKey: ["users", companyId] });
    },
  });

  const revokePermission = async (
    bearerToken: string,
    payload: RevokePermissionsBody,
    companyId: number
  ) => {
    return await revokeUserPermission(
      bearerToken as string,
      payload,
      companyId
    );
  };

  return {
    deleteCompanyUser,
    deletingUser,
  };
};

export default useDeleteCompanyUser;
